import { useAuth } from '@wui/common-library'
import axios, { AxiosInstance } from 'axios'
import { watch } from 'vue'

import { version as appVersion } from '@/../package.json'

import { useBaseURL } from '@/shared/composables/url'
import { getPageIdForRequest } from '@/shared/utils/page-id'
import { PromiseQueue } from '@/shared/utils/promise'

const X_APP_VERSION_HEADER = 'X-App-Version'

let axiosInstance: AxiosInstance
const awaitingForAccessTokenRequests = new PromiseQueue()

const SYNC_REQUESTS_OMIT_PAGE_ID_HEADER = [
  '/api/user/who-am-i',
  '/api/administrative-group',
  '/api/feature',
]

const initInterceptors = () => {
  axiosInstance.interceptors.request.use((config) => {
    const { token } = useAuth()

    config.headers[X_APP_VERSION_HEADER] = appVersion
    if (token.value) {
      config.headers.Authorization = `Bearer ${token.value}`
    } else {
      delete config.headers.Authorization
    }

    if (SYNC_REQUESTS_OMIT_PAGE_ID_HEADER.find((path) => config.url?.endsWith(path))) {
      // ignore
    } else {
      config.headers.pageId = getPageIdForRequest()
    }
    return config
  })

  axiosInstance.interceptors.response.use((response) => {
    return response
  }, async (error) => {
    if (error?.response?.status === 401) {
      const { token } = useAuth()
      // Queueing 401 without token causes hanging auth flows
      // (for example, cancelled Windows/Kerberos auth must fall back to login form).
      if (!token.value) {
        return Promise.reject(error)
      }
      const config = error.config
      return new Promise((resolve, reject) => {
        awaitingForAccessTokenRequests.add({
          resolve: async (): Promise<void> => {
            try {
              const data = await axiosInstance.request(config)
              resolve(data)
            } catch (e) {
              reject(e)
            }
          },
          reject,
        })
      })
    }
    return Promise.reject(error)
  })
}

export const useAxios = () => {
  const { token } = useAuth()

  if (!axiosInstance) {
    const { baseURL } = useBaseURL()

    axiosInstance = axios.create({
      baseURL,
    })

    initInterceptors()

    watch(() => token.value, (value) => {
      if (value) {
        awaitingForAccessTokenRequests.resolveAll()
      } else {
        awaitingForAccessTokenRequests.rejectAll()
      }
    })
  }

  return {
    axiosInstance,
  }
}
